import {
  Box,
  debounce,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { CopyToClipboardButton, DynamicIcon, Tooltip } from 'src/components';
import { AutocompleteSearchBar } from 'src/components/SearchBar/autocomplete-search-bar';
import { SideTabStyledWrapper } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Address, Conversation, useAddress } from 'src/graphql';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { Modals, useModalMethods } from 'src/store/modals';
import {
  useInViewTrigger,
  useNotifications,
  useOpenSite,
  useScrollPaginateHandler,
} from 'src/utils/hooks';
import { ConversationSite } from './conversation-site';
import { useConversationAddress } from './use-conversation-address';
import { useConversationAssociatedSites } from './use-conversation-associated-sites';

interface ConversationSitesProps {
  conversation: Conversation;
  /** Lets the hosting card show the "+N more" chip in its header. */
  onAvailableSitesCountChange?: (count: number) => void;
}

const SCROLL_THRESHOLD_PX = 80;

const AddressItemSkeleton = () => (
  <Stack
    ml={1}
    overflow="hidden"
    flexGrow={1}
    direction="row"
    spacing={1}
    alignItems="center"
    padding="0.8rem"
  >
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton width="70%" />
  </Stack>
);

export const ConversationSites = ({
  conversation,
  onAvailableSitesCountChange,
}: ConversationSitesProps) => {
  const theme = useTheme();
  const [input, setInput] = useState('');
  const [searchTerm, setSearchTerm] = useState<null | string>('');

  const { showSuccessAlert } = useNotifications();

  const { showModal: showConversationToAddressModal } = useModalMethods(
    Modals.UpdateConversationToAddress,
  );

  const conversationAddress = useConversationAddress(conversation);

  const {
    addresses: clientAddresses,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    updateAssociatedSitesCache,
    filters,
  } = useConversationAssociatedSites(conversation, input);

  const filteredSites = useMemo(
    () => [conversationAddress, ...clientAddresses].filter((site) => !!site),
    [conversationAddress, clientAddresses],
  );

  const associatedAddressesCount = useMemo(() => {
    const uniqueAddressIds = new Set(
      [conversationAddress, ...clientAddresses]
        .filter((site): site is Address => !!site)
        .map((site) => site.addressId),
    );
    return uniqueAddressIds.size;
  }, [clientAddresses, conversationAddress]);

  useEffect(() => {
    onAvailableSitesCountChange?.(conversationAddress ? associatedAddressesCount : 0);
  }, [associatedAddressesCount, conversationAddress, onAvailableSitesCountChange]);

  const initialFilters = [
    // This filter should be present, since it is also used in multi-tenancy mode
    // when partner-id header is not included in the request.
    {
      attribute: 'partnerId',
      operator: 'in',
      value: [conversation?.partnerId, PLATFORM_PARTNER_ID].join(','),
    },
  ];

  const {
    records: sitesToAdd,
    loadMore: loadMoreSearch,
    hasMore: hasMoreSearch,
    isLoading: isLoadingSearch,
  } = useSearch({
    entityName: EntityNames.Site,
    searchTerm,
    filter: filteredSites.length
      ? [
          {
            attribute: 'addressId',
            operator: 'not_in',
            value: filteredSites.map((site) => site.addressId).join(','),
          },
          ...initialFilters,
        ]
      : initialFilters,
  }) ?? {};

  const filteredSitesToAdd = useMemo(
    () =>
      sitesToAdd
        // Remove duplicated elements
        ?.filter(
          (site, index, arr) => arr.findIndex((s) => s.addressId === site.addressId) === index,
        )
        .map((site) => ({
          label: site.fullAddress ?? '',
          ...site,
        })) ?? [],
    [sitesToAdd],
  );

  const inViewElementRef = useInViewTrigger({
    onEnter: loadMore,
    enabled: !loading && !!hasMore && !isLoadingMore,
  });

  const handleAutocompleteScroll = useScrollPaginateHandler({
    loadMore: loadMoreSearch,
    enabled: hasMoreSearch,
    thresholdPx: SCROLL_THRESHOLD_PX,
  });

  const { createConversationToAddress, createClientToAddress } = useAddress(
    conversationAddress?.addressId,
  );

  const { showModal } = useModalMethods(Modals.AddSiteModal);

  const openSite = useOpenSite();

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setInput(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onTextFieldChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchTerm(target.value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const onCreateButtonClick = useCallback(() => {
    showModal();
  }, [showModal]);

  const requesterEntity = conversation.requester
    ?.participantEntity as ConversationParticipantEntity;

  const onSelectSite = useCallback(
    async (newValue: Address | null) => {
      if (!newValue) {
        return;
      }

      if (
        !clientAddresses.find((it) => it.addressId === newValue.addressId) &&
        requesterEntity === ConversationParticipantEntity.Client &&
        conversation.requester.participantIdentity !== 'unknown'
      ) {
        await createClientToAddress({
          client: {
            ...(conversation.requester?.client ?? {}),
            id:
              conversation.requester?.client?.clientId ??
              conversation.requester?.participantIdentity,
            clientId:
              conversation.requester?.client?.clientId ??
              conversation.requester?.participantIdentity,
          },
          address: newValue,
          partnerId: conversation.partnerId,
          filters,
        });
        updateAssociatedSitesCache(newValue);
      }

      const response = await createConversationToAddress(
        conversation.conversationId,
        newValue,
        conversation.partnerId,
      );

      if (!response) {
        return;
      }

      setSearchTerm('');
      setInput('');

      showSuccessAlert('Site was added to the conversation');
    },
    [
      clientAddresses,
      conversation.conversationId,
      conversation.partnerId,
      conversation.requester?.client,
      conversation.requester.participantIdentity,
      createClientToAddress,
      createConversationToAddress,
      filters,
      requesterEntity,
      showSuccessAlert,
      updateAssociatedSitesCache,
    ],
  );

  const onAddButtonClick = useCallback(() => {
    showConversationToAddressModal({
      conversation,
      onSelect: (_, { clientAddress = null, siteToAdd = null }) => {
        const newValue = clientAddress || siteToAdd;
        if (!newValue) {
          return;
        }
        void onSelectSite(newValue);
      },
    });
  }, [conversation, onSelectSite, showConversationToAddressModal]);

  const onAutocompleteChange = useCallback(
    async (_: SyntheticEvent, newValue: Address | null) => {
      await onSelectSite(newValue);
    },
    [onSelectSite],
  );

  if (conversationAddress) {
    return <ConversationSite conversation={conversation} />;
  }

  if (clientAddresses.length || input || loading) {
    return (
      <SideTabStyledWrapper>
        <Typography variant="subtitle2" paddingBottom={1} fontSize="0.75rem">
          Select Associated Site
        </Typography>
        <AutocompleteSearchBar
          addButtonText="+ Add Site"
          onAddButtonClick={onAddButtonClick}
          createButtonText="+ Create Site"
          searchPlaceholder="Find & Associate Existing Site"
          filteredEntities={filteredSitesToAdd}
          isLoading={isLoadingSearch}
          handleScroll={handleAutocompleteScroll}
          onAutocompleteTextFieldChange={onTextFieldChange}
          onAutocompleteChange={onAutocompleteChange}
          onCreateButtonClick={onCreateButtonClick}
          handleOnFilterResultsChange={handleOnChange}
        />
        <Box display="grid">
          {!!loading && !clientAddresses.length && <AddressItemSkeleton />}
          {clientAddresses.map((clientAddress) => (
            <Stack
              key={clientAddress.addressId}
              direction="row"
              spacing={1}
              alignItems="center"
              overflow="hidden"
              width="100%"
            >
              <Tooltip title="Associate address" hideWhenRefIsHidden>
                <IconButton
                  onClick={async () => {
                    await createConversationToAddress(
                      conversation.conversationId,
                      clientAddress,
                      conversation.partnerId,
                    );
                    setSearchTerm('');
                    setInput('');
                  }}
                >
                  <DynamicIcon
                    name="add_circle"
                    filled
                    color={theme.palette.custom.OV.sys.secondary}
                  />
                </IconButton>
              </Tooltip>
              <Stack direction="row" spacing={1} alignItems="center" flexGrow={1} minWidth={0}>
                <SiteAddress
                  address={clientAddress}
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    openSite(event, clientAddress)
                  }
                  tooltipText="Click to open site"
                />
                <Box flexGrow={1} />
                <CopyToClipboardButton content={clientAddress.fullAddress!} />
              </Stack>
            </Stack>
          ))}
          {!!isLoadingMore && <AddressItemSkeleton />}
          <Box ref={inViewElementRef} sx={{ height: 2 }} />
          {!loading && !clientAddresses?.length && (
            <Stack padding="0.5rem">
              <Typography alignSelf="center" variant="body2">
                No results
              </Typography>
            </Stack>
          )}
        </Box>
      </SideTabStyledWrapper>
    );
  }

  return (
    <SideTabStyledWrapper>
      <Typography variant="subtitle2" paddingBottom={1} fontSize="0.75rem">
        Associated Site
      </Typography>
      <AutocompleteSearchBar
        addButtonText="+ Add Site"
        onAddButtonClick={onAddButtonClick}
        createButtonText="+ Create Site"
        searchPlaceholder="Find & Associate Existing Site"
        filteredEntities={filteredSitesToAdd}
        isLoading={isLoadingSearch}
        handleScroll={handleAutocompleteScroll}
        onAutocompleteTextFieldChange={onTextFieldChange}
        onAutocompleteChange={onAutocompleteChange}
        onCreateButtonClick={onCreateButtonClick}
        handleOnFilterResultsChange={handleOnChange}
        hideSearchBar
      />
    </SideTabStyledWrapper>
  );
};
